import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { message } from "antd";
import {
  ProductDetails,
  ProductName,
  QuantitySelector,
  SkuAndCase,
} from "../catalogue/components/ProductCard.styles";
import { Status } from "../../newLayout/page/components/Status/Status";
import { BackArrow, Minus, Plus, SubmitIcon } from "../assets/icons";
import { ReactComponent as DeleteDanger } from "../../newLayout/assets/svgIcon/shipment/DeleteDanger.svg";
import {
  StyledCartPageContainerDiv,
  StyledContinueBrowsingLink,
  StyledCartMainContentDiv,
  StyledCartTableContainerDiv,
  StyledCartTableHeaderContainerDiv,
  StyledCartTableHeaderDiv,
  StyledCartTableBodyDiv,
  StyledCartTableRowDiv,
  StyledCartTableRowItemDiv,
  StyledRemoveButton,
  StyledCartSidebarContainerDiv,
  StyledSidebarCard,
  StyledSidebarHeader,
  StyledSummaryRow,
  StyledSubmitButton,
  StyledSidebarContinueBrowsingButton,
  StyledDisclaimer,
  StyledFormGroup,
} from "./index.styles";
import { Info } from "../../../assets/icons";
import { Topbar } from "../catalogue/topbar/Topbar";
import { StyledHeaderDiv } from "../catalogue/index.styles";
import { B2bFooter } from "../components/layout/footer";

import { usePortalCart } from "../hooks/usePortalCart";
import { getStockStatus, isOutOfStock } from "../utils/stockUtils";
import { CartSkeleton } from "./components/CartSkeleton";
import { CheckoutSuccessModal } from "./components/CheckoutSuccessModal";
import dayjs from "dayjs";
import { selectPortalSession } from "../../../store/slice/portalAuthSlice";
import { usePortalFormattedAmount } from "../hooks/usePortalFormattedAmount";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  FormControlInput,
  FormControlDate,
  FormControlSearchableSelectAPI,
} from "../../../components/organisms/Form/control";
import { CartEmptyState } from "./index.styles";

const validationSchema = Yup.object().shape({
  shipping_address_id: Yup.mixed().required("Shipping address is required"),
  requested_delivery_date: Yup.date().required("Requested delivery date is required"),
});

export const Cart = () => {
  const navigate = useNavigate();
  const { subdomain } = useParams();
  const session = useSelector(state => selectPortalSession(state, subdomain));

  const currencySymbol = session?.currency?.symbol || "$";
  const currencyId = session?.currency?.id;
  const currencyName = session?.currency?.name || session?.currency?.code || "Unknown Currency";
  const paymentTermLabel = session?.paymentTerm?.name;

  const formatAmount = usePortalFormattedAmount();

  const {
    items,
    itemCount,
    subtotal,
    taxTotal,
    total,
    isLoading,
    updateItem,
    removeItem,
    checkout,
    updatingItemId,
    removingItemId,
    isCheckingOut,
  } = usePortalCart();

  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
  const [lastSalesNo, setLastSalesNo] = useState(null);

  const handleQtyChange = async (item, newQty) => {
    const stock = item.available_stock;
    if (newQty < 1) return;
    if (stock != null && newQty > Number(stock)) {
      message.warning(`Cannot increase because available stock is ${stock}`);
      return;
    }

    const res = await updateItem(item.id, newQty);
    if (!res.success) {
      message.error(res.error);
    }
  };

  const handleRemoveItem = async itemId => {
    const res = await removeItem(itemId);
    if (res.success) {
      message.success("Item removed from cart");
    } else {
      message.error(res.error);
    }
  };

  const handleSubmitOrder = async (values, { setErrors }) => {
    if (items.length === 0) {
      message.warning("Your cart is empty.");
      return;
    }
    if (!currencyId) {
      message.error("Currency is missing from your session. Cannot proceed.");
      return;
    }

    const res = await checkout({
      currency_id: currencyId,
      shipping_address_id: values.shipping_address_id,
      po_number: values.po_number || undefined,
      requested_delivery_date: values.requested_delivery_date.format("YYYY-MM-DD"),
      special_instructions: values.special_instructions || undefined,
    });

    if (res.success) {
      setLastSalesNo(res.salesNo);
      setIsSuccessModalVisible(true);
    } else {
      message.error(res.error);
      setErrors(res.errorData);
    }
  };

  const handleContinueBrowsing = () => {
    navigate(`/portal/${subdomain}/catalog`);
  };

  const handleViewOrders = () => {
    setIsSuccessModalVisible(false);
    navigate(`/portal/${subdomain}/orders`);
  };

  if (isLoading) {
    return (
      <>
        <Topbar cartItemCount={itemCount} cartTotal={total} showSearch={false} />
        <CartSkeleton />
        <B2bFooter />
      </>
    );
  }

  return (
    <>
      <Topbar cartItemCount={itemCount} cartTotal={total} showSearch={false} />
      <StyledCartPageContainerDiv>
        {/* Continue Browsing Breadcrumb */}
        <StyledContinueBrowsingLink onClick={handleContinueBrowsing}>
          <BackArrow />
          <span>Continue Browsing</span>
        </StyledContinueBrowsingLink>

        {/* Title */}
        <StyledHeaderDiv style={{ marginBottom: "20px" }}>Review your order</StyledHeaderDiv>

        {/* Main Content Layout */}
        <StyledCartMainContentDiv>
          {/* Left Table Column */}
          <StyledCartTableContainerDiv>
            <StyledCartTableHeaderContainerDiv>
              <StyledCartTableHeaderDiv className="product">Product</StyledCartTableHeaderDiv>
              <StyledCartTableHeaderDiv className="unit-price">Unit Price</StyledCartTableHeaderDiv>
              <StyledCartTableHeaderDiv className="qty">Qty</StyledCartTableHeaderDiv>
              <StyledCartTableHeaderDiv className="total">Total</StyledCartTableHeaderDiv>
            </StyledCartTableHeaderContainerDiv>

            <StyledCartTableBodyDiv>
              {items.length === 0 ? (
                <CartEmptyState>Your cart is empty.</CartEmptyState>
              ) : (
                items.map(item => {
                  const product = item.product || {};
                  const stockStatus = getStockStatus(item.available_stock);
                  const outOfStock = isOutOfStock(item.available_stock);

                  const isDecrementDisabled =
                    outOfStock || item.quantity <= 1 || updatingItemId === item.id;
                  const isIncrementDisabled =
                    outOfStock ||
                    (item.available_stock != null &&
                      item.quantity >= Number(item.available_stock)) ||
                    updatingItemId === item.id;

                  const incrementTitle = outOfStock
                    ? "Cannot increase quantity because this product is out of stock"
                    : item.available_stock != null && item.quantity >= Number(item.available_stock)
                      ? `Cannot increase because available stock is ${item.available_stock}`
                      : "Increase quantity";

                  const decrementTitle = outOfStock
                    ? "Cannot decrease quantity because this product is out of stock"
                    : "Decrease quantity";

                  return (
                    <StyledCartTableRowDiv
                      key={item.id}
                      style={{ opacity: removingItemId === item.id ? 0.5 : 1 }}
                    >
                      <StyledCartTableRowItemDiv className="product">
                        <ProductDetails>
                          <SkuAndCase>
                            <span className="sku">SKU {product.number || "N/A"}</span>
                          </SkuAndCase>
                          <ProductName>{product.name}</ProductName>
                          <Status statusName={stockStatus.label} />
                        </ProductDetails>
                      </StyledCartTableRowItemDiv>

                      <StyledCartTableRowItemDiv className="unit-price">
                        {currencySymbol}
                        {formatAmount(item.unit_price || 0)}
                      </StyledCartTableRowItemDiv>

                      <StyledCartTableRowItemDiv className="qty">
                        <QuantitySelector style={{ margin: "0 auto" }}>
                          <button
                            onClick={() => handleQtyChange(item, Number(item.quantity) - 1)}
                            disabled={isDecrementDisabled}
                            title={decrementTitle}
                            aria-label="Decrease quantity"
                          >
                            <Minus />
                          </button>
                          <span className="quantity-value">
                            {Math.round(Number(item.quantity))}
                          </span>
                          <button
                            onClick={() => handleQtyChange(item, Number(item.quantity) + 1)}
                            disabled={isIncrementDisabled}
                            title={incrementTitle}
                            aria-label="Increase quantity"
                          >
                            <Plus />
                          </button>
                        </QuantitySelector>
                      </StyledCartTableRowItemDiv>

                      <StyledCartTableRowItemDiv className="total">
                        <span className="total-value">
                          {currencySymbol}
                          {formatAmount(item.subtotal || 0)}
                        </span>
                        <StyledRemoveButton
                          onClick={() => handleRemoveItem(item.id)}
                          disabled={removingItemId === item.id}
                        >
                          <span>{removingItemId === item.id ? "Removing..." : "Remove"}</span>
                          <DeleteDanger />
                        </StyledRemoveButton>
                      </StyledCartTableRowItemDiv>
                    </StyledCartTableRowDiv>
                  );
                })
              )}
            </StyledCartTableBodyDiv>
          </StyledCartTableContainerDiv>

          {/* Right Sidebar Column */}
          <StyledCartSidebarContainerDiv>
            <Formik
              initialValues={{
                shipping_address_id: undefined,
                po_number: "",
                requested_delivery_date: dayjs(),
                currency: currencyName,
                special_instructions: "",
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmitOrder}
              validateOnBlur
            >
              {({ values, handleChange, handleSubmit, isValid }) => (
                <Form style={{ display: "contents" }}>
                  {/* Order Summary Card */}
                  <StyledSidebarCard>
                    <StyledSidebarHeader>Order Summary</StyledSidebarHeader>

                    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                      <StyledSummaryRow className="subtotal">
                        <span className="label">Subtotal</span>
                        <span className="value">
                          {currencySymbol}
                          {formatAmount(subtotal)}
                        </span>
                      </StyledSummaryRow>

                      <StyledSummaryRow className="tax">
                        <span className="label">Tax</span>
                        <span className="value">
                          {currencySymbol}
                          {formatAmount(taxTotal)}
                        </span>
                      </StyledSummaryRow>
                    </div>

                    <StyledSummaryRow className="total">
                      <span className="label">Total</span>
                      <span className="value">
                        {currencySymbol}
                        {formatAmount(total)}
                      </span>
                    </StyledSummaryRow>

                    <StyledSubmitButton
                      type="button"
                      onClick={handleSubmit}
                      disabled={!isValid || items.length === 0 || isCheckingOut}
                    >
                      <SubmitIcon />
                      <span>{isCheckingOut ? "Submitting..." : "Submit"}</span>
                    </StyledSubmitButton>

                    <StyledSidebarContinueBrowsingButton
                      type="button"
                      onClick={handleContinueBrowsing}
                    >
                      Continue Browsing
                    </StyledSidebarContinueBrowsingButton>

                    <StyledDisclaimer>
                      <Info />
                      <span>
                        {paymentTermLabel ? (
                          <>
                            No payment is collected — your account is on{" "}
                            <strong>{paymentTermLabel}</strong> terms.
                          </>
                        ) : (
                          <>
                            No payment is collected at checkout. Terms follow your account
                            agreement.
                          </>
                        )}
                      </span>
                    </StyledDisclaimer>
                  </StyledSidebarCard>

                  {/* Order Details Card */}
                  <StyledSidebarCard>
                    <StyledSidebarHeader style={{ marginBottom: "20px" }}>
                      Order Details
                    </StyledSidebarHeader>

                    <StyledFormGroup>
                      <FormControlInput
                        name="currency"
                        label="Currency"
                        readOnly={true}
                        context="formik"
                      />
                    </StyledFormGroup>

                    <StyledFormGroup>
                      <label htmlFor="po_number">PO / Reference Number</label>
                      <input
                        id="po_number"
                        name="po_number"
                        type="text"
                        placeholder="e.g. PO-202400042"
                        value={values.po_number}
                        onChange={handleChange}
                      />
                    </StyledFormGroup>

                    <StyledFormGroup>
                      <FormControlDate
                        name="requested_delivery_date"
                        label="Requested Delivery Date"
                        context="formik"
                        required
                      />
                    </StyledFormGroup>

                    <StyledFormGroup>
                      <FormControlSearchableSelectAPI
                        name="shipping_address_id"
                        moduleName="portalAddresses"
                        label="Shipping Address"
                        placeholder="Select a shipping address"
                        context="formik"
                        required
                      />
                    </StyledFormGroup>

                    <StyledFormGroup>
                      <label htmlFor="special_instructions">Special Instructions</label>
                      <textarea
                        id="special_instructions"
                        name="special_instructions"
                        placeholder="Delivery notes, loading instructions, or any other requirements..."
                        value={values.special_instructions}
                        onChange={handleChange}
                      />
                    </StyledFormGroup>
                  </StyledSidebarCard>
                </Form>
              )}
            </Formik>
          </StyledCartSidebarContainerDiv>
        </StyledCartMainContentDiv>
      </StyledCartPageContainerDiv>
      {/* Footer */}
      <B2bFooter />

      <CheckoutSuccessModal
        visible={isSuccessModalVisible}
        salesNo={lastSalesNo}
        onClose={() => setIsSuccessModalVisible(false)}
        onViewOrders={handleViewOrders}
        onContinueBrowsing={handleContinueBrowsing}
      />
    </>
  );
};
